import { UseFormRegister } from "react-hook-form";
import { useFormStep } from "./formContext";

interface PricingSummaryProps {
  optionPrice: string;
  // VAT: string;
  taxBreakdown: { label: string; percentage: number; amount: number }[];
  numPmts: number;
  register: UseFormRegister<any>;
  getValues: Function;
  hideBookingSummary?: boolean;
  hidePaymentSchedule?: boolean;
  couponState?: {
    code?: string;
    error?: string;
    discountType?: "relative" | "absolute";
    discountValue?: number;
    duration?: number | null;
  };
  isRoomMode?: boolean;
  isDesktop?: boolean;
}

const Euro = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "EUR",
});

export default function PricingSummary({
  optionPrice,
  // VAT,
  taxBreakdown,
  numPmts,
  register,
  getValues,
  hideBookingSummary = false,
  hidePaymentSchedule = false,
  couponState,
  isRoomMode = false,
  isDesktop = false,
}: PricingSummaryProps) {
  const formStep = useFormStep();
  const fontSize = isDesktop ? "text-sm" : "text-xs";
  const priceFont = isDesktop ? "text-base" : "text-sm";
  const spacing = isDesktop ? "px-3 py-2" : "px-2 py-1";
  const effectiveStep = isDesktop ? formStep : formStep - 1;

  let optionPriceCents = 0;
  if (typeof optionPrice === "string") {
    optionPriceCents = parseFloat(optionPrice.replace(/[^0-9.-]+/g, "")) * 100;
  }

  let reduction = 0;
  let discountedPriceCents = optionPriceCents;

  if (couponState?.code && couponState?.discountType && couponState?.discountValue) {
    if (couponState.discountType === "relative") {
      reduction = Math.round(optionPriceCents * (couponState.discountValue / 100));
    } else if (couponState.discountType === "absolute") {
      if (couponState.duration == null) {
        // per booking
        reduction = couponState.discountValue;
      } else {
        // per N nights
        const nights = isRoomMode
          ? parseInt(getValues("Experience") || "1", 10) // suite nights
          : getValues("Experience") === "0"
            ? 3
            : 5; // class length
        const periods = Math.floor(nights / couponState.duration);
        reduction = couponState.discountValue * periods;
      }
    }
    discountedPriceCents = optionPriceCents - reduction;
  }

  // 🔎 Recalculate tax breakdown based on discounted price
  let adjustedTaxBreakdown = taxBreakdown;
  if (taxBreakdown.length > 0 && discountedPriceCents !== optionPriceCents) {
    const totalPrice = discountedPriceCents / 100;
    const combinedRate = taxBreakdown.reduce((sum, tax) => sum + tax.percentage, 0);
    const basePrice = totalPrice / (1 + combinedRate / 100);

    adjustedTaxBreakdown = taxBreakdown.map((tax) => ({
      ...tax,
      amount: Math.round(basePrice * (tax.percentage / 100) * 100) / 100,
    }));
  }

  // 🔎 Payment schedule recalculated against discounted total
  const totalForSchedule = couponState?.code ? discountedPriceCents : optionPriceCents;

  return (
    <div className={`w-full border border-gray-300 rounded-lg ${spacing} bg-gray-50 ${fontSize} text-gray-800 space-y-2`}>
      {/* 🧾 Booking Summary */}
      {!hideBookingSummary && (
        <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 text-sm text-gray-700 text-center">
          {Array.isArray(getValues("dates")) && getValues("dates").length === 2 && getValues("ExperienceTitle") ? (
            <>
              <span>
                <strong>{["0", "1"].includes(getValues("Experience")) ? "Class" : "Room"}:</strong> {getValues("ExperienceTitle")}
              </span>

              {["0", "1"].includes(getValues("Experience")) ? (
                <span>
                  <strong>Length:</strong>{" "}
                  {getValues("Experience") === "0" ? "3-Day" : getValues("Experience") === "1" ? "5-Day" : "All Class Lengths"}
                </span>
              ) : (
                <span>
                  <strong>Nights:</strong> {getValues("Experience")}
                </span>
              )}

              <span>
                <strong>Dates:</strong> {`${getValues("dates")[0].toLocaleDateString()} – ${getValues("dates")[1].toLocaleDateString()}`}
              </span>
              <span>
                <strong>Guests:</strong> {getValues("NumberofGuests")}
              </span>
            </>
          ) : (
            <span className="italic text-center">Pricing will appear once dates are selected</span>
          )}
        </div>
      )}

      {/* 💰 Price Summary */}
      <div
        className={`rounded-md px-2 py-1 font-semibold flex flex-col items-center gap-y-1 min-h-[20px] ${priceFont}`}
        style={{
          backgroundColor: getValues("ExperienceTitle") && getValues("dates")?.length === 2 ? "#696b69" : "#F3F3F3",
          color: getValues("ExperienceTitle") && getValues("dates")?.length === 2 ? "#FFFFFF" : "#999999",
        }}
      >
        {getValues("ExperienceTitle") && getValues("dates")?.length === 2 ? (
          <>
            {/* Coupon logic using effectiveStep */}
            {((isRoomMode && effectiveStep === 2) || (!isRoomMode && effectiveStep === 3)) && couponState?.code && reduction > 0 ? (
              <>
                {/* Original + Coupon */}
                <span className="flex flex-wrap justify-center items-center gap-x-2">
                  <span className="line-through opacity-70 text-md">Price: {optionPrice}</span>
                  <span className="text-sm text-white/90">
                    Coupon <strong>{couponState.code}</strong> applied – {Euro.format(reduction / 100)} off
                  </span>
                </span>

                {/* New Price */}
                <span className="text-md font-bold">
                  New Price: {Euro.format(discountedPriceCents / 100)}
                  {adjustedTaxBreakdown.length > 0 && (
                    <span className="text-sm font-normal text-white/90 ml-2">
                      (incl. {Euro.format(adjustedTaxBreakdown[0].amount)} VAT)
                    </span>
                  )}
                </span>
              </>
            ) : (
              <>
                {/* Original price only */}
                <span className="flex flex-wrap justify-center items-center gap-x-2 text-md">
                  <span>Price: {optionPrice}</span>
                  {adjustedTaxBreakdown.length > 0 && (
                    <span className="text-sm text-white/90">(incl. {Euro.format(adjustedTaxBreakdown[0].amount)} VAT)</span>
                  )}
                </span>
              </>
            )}
          </>
        ) : (
          <span className="italic text-center">Pricing will appear once dates are selected</span>
        )}
      </div>

      {/* 📆 Payment Schedule */}
      {!hidePaymentSchedule && (
        <div className="min-h-[20px] flex flex-wrap justify-center items-center gap-x-3 text-md text-gray-700">
          {getValues("ExperienceTitle") && getValues("dates")?.length === 2 && numPmts > 0 ? (
            [...Array(numPmts)].map((_, i) => {
              const pct = getValues(`pct${i + 1}`) || 100 / numPmts; // fallback to equal split
              const installment = Math.round(totalForSchedule * (pct / 100));
              return (
                <span key={i} className="inline-block">
                  <span className="font-medium">Payment {i + 1}:</span> {Euro.format(installment / 100)} ({pct}%)–{""}
                  <span className="text-gray-500">
                    Due{" "}
                    {new Date(getValues(`due${i + 1}`)).toDateString() === new Date().toDateString()
                      ? "Today"
                      : new Date(getValues(`due${i + 1}`)).toLocaleDateString("en-US")}
                  </span>
                </span>
              );
            })
          ) : (
            <span className="italic text-center w-full text-gray-400"></span>
          )}
        </div>
      )}
    </div>
  );
}
